import { useState, useEffect, useRef, useMemo } from "react";
import { hapticMedium } from "../utils/haptics";
import { createPortal } from "react-dom";
import { RADIUS } from "../theme/tokens";
import { useThemeContext } from "../theme/ThemeContext";
import { useWindowWidth } from "../hooks/useWindowWidth";
import { LHG_CARRIERS, AirlineLogo, StarMark, Button } from "./shared";
import { fmtConn, getAirlineCode } from "../utils/timeUtils";
import { getMctRisk } from "../utils/mctUtils";
import { terminalFallback, REMARK_PATTERN } from "../utils/entryUtils";
import { IATA_TO_ICAO } from "../utils/flightTrackingUtils";
import FlightRouteMap from "./FlightRouteMap";

const toICAO = (iata) => IATA_TO_ICAO[(iata || "").toUpperCase()] || (iata || "").toUpperCase();

function useCurrentTime() {
  const [now, setNow] = useState(() => new Date());
  useEffect(() => {
    const id = setInterval(() => setNow(new Date()), 30000);
    return () => clearInterval(id);
  }, []);
  return now;
}

// -- Entry card ---------------------------------------------------------------
// Static style constants (no theme/prop dependencies — hoisted to avoid per-render allocation)
const STYLE_ENTRY_OUTER       = { position: "relative", marginBottom: 0, borderRadius: RADIUS.card, breakInside: "avoid", overflow: "visible", contain: "layout" };

// Module-level ref so any card can close the currently open context menu
// before opening its own — prevents overlapping menus on long-press.
let _closeActiveMenu = null;

function EntryCard({ entry, onDelete, onEdit, onReopen, onMarkSuccessful, onView, isUA, onRemark, onBoarded, onWillHold, onMessage, onPin, onMarkSeen, onAmenityRequest = undefined, amenityStatus = null, isNew = false, isImported = false, myAirline = null, appDate = null, onHide = undefined }) {
  const { isDark, SA, STATUS_STYLES, GLASS, tokens, TYPOGRAPHY } = useThemeContext();
  const winWidth = useWindowWidth();
  const isWide = winWidth >= 768;
  const s = useMemo(
    () => STATUS_STYLES[entry.status] || { bg: SA.darkGray, border: SA.borderGray, text: SA.silver, dot: SA.borderGray },
    [entry.status, STATUS_STYLES, SA]
  );
  const statusBadgeStyle = useMemo(() => ({
    backdropFilter: "blur(10px)", WebkitBackdropFilter: "blur(10px)",
    background: s.bg, color: s.text, border: `1px solid ${s.border}`,
    padding: "5px 11px", fontSize: TYPOGRAPHY.caption, fontWeight: 700, letterSpacing: "0.22em",
    textTransform: "uppercase", fontFamily: "'HelveticaNeueStd','Barlow','Helvetica Neue',Arial,sans-serif",
    display: "flex", alignItems: "center", gap: 5, borderRadius: 6,
  }), [s, TYPOGRAPHY]);
  const editBtnStyle = useMemo(() => ({
    background: SA.medGray, border: `1px solid ${SA.borderGray}`, color: SA.silver,
    fontFamily: "'HelveticaNeueStd','Barlow','Helvetica Neue',Arial,sans-serif", fontSize: TYPOGRAPHY.caption, letterSpacing: "0.22em",
    textTransform: "uppercase", padding: "6px 16px", borderRadius: RADIUS.pill, cursor: "pointer",
  }), [SA, TYPOGRAPHY]);
  const partnerExpandBtnStyle = useMemo(() => ({
    background: tokens.partnerSurface, border: `1px solid ${tokens.partnerBorder}`,
    color: tokens.partner, fontFamily: "'HelveticaNeueStd','Barlow','Helvetica Neue',Arial,sans-serif", fontSize: TYPOGRAPHY.caption,
    letterSpacing: "0.22em", textTransform: "uppercase", padding: "6px 16px",
    borderRadius: RADIUS.pill, cursor: "pointer",
  }), [tokens, TYPOGRAPHY]);
  // Tray button styles — depend on tokens so they adapt to light/dark
  const styleDeleteBtn        = useMemo(() => ({ flex: 1, background: tokens.dangerSurface,  border: "none", color: tokens.dangerText,  fontSize: TYPOGRAPHY.caption, letterSpacing: "0.22em", textTransform: "uppercase", fontFamily: "'HelveticaNeueStd','Barlow','Helvetica Neue',Arial,sans-serif", cursor: "pointer" }),                         [tokens, TYPOGRAPHY]);
  const styleConfirmDeleteBtn = useMemo(() => ({ flex: 1, background: tokens.dangerSurface2, border: "none", color: tokens.dangerText2, fontSize: TYPOGRAPHY.caption, letterSpacing: "0.22em", textTransform: "uppercase", fontFamily: "'HelveticaNeueStd','Barlow','Helvetica Neue',Arial,sans-serif", cursor: "pointer", fontWeight: 700 }),        [tokens, TYPOGRAPHY]);
  const stylePartnerTrayBtn   = useMemo(() => ({ flex: 1, background: tokens.partnerSurface, border: "none", color: tokens.partner,     fontSize: TYPOGRAPHY.caption, letterSpacing: "0.22em", textTransform: "uppercase", fontFamily: "'HelveticaNeueStd','Barlow','Helvetica Neue',Arial,sans-serif", cursor: "pointer", fontWeight: 600 }),        [tokens, TYPOGRAPHY]);
  const styleOverdueBadge     = useMemo(() => ({ background: tokens.dangerBg, color: tokens.danger, border: `1px solid ${tokens.dangerBorder}`, padding: "5px 11px", fontSize: TYPOGRAPHY.caption, fontWeight: 700, letterSpacing: "0.22em", textTransform: "uppercase", fontFamily: "'HelveticaNeueStd','Barlow','Helvetica Neue',Arial,sans-serif", display: "flex", alignItems: "center", gap: 6, borderRadius: 1 }), [tokens, TYPOGRAPHY]);
  const styleMonitoringBadge  = useMemo(() => ({ background: "transparent", color: tokens.warning, border: `1px solid ${tokens.warningBorder}`, padding: "5px 11px", fontSize: TYPOGRAPHY.caption, letterSpacing: "0.22em", textTransform: "uppercase", fontFamily: "'HelveticaNeueStd','Barlow','Helvetica Neue',Arial,sans-serif", display: "flex", alignItems: "center", gap: 5, borderRadius: 1 }), [tokens, TYPOGRAPHY]);
  const styleMonitoringDot    = useMemo(() => ({ width: 6, height: 6, borderRadius: RADIUS.circle, background: tokens.warning, display: "inline-block" }), [tokens]);
  const styleMonoGreen        = useMemo(() => ({ fontSize: TYPOGRAPHY.caption, color: tokens.monoGreen,  fontFamily: "'HelveticaNeueStd','Barlow','Helvetica Neue',Arial,sans-serif" }), [tokens, TYPOGRAPHY]);
  const styleMonoPurple       = useMemo(() => ({ fontSize: TYPOGRAPHY.caption, color: tokens.monoPurple, fontFamily: "'HelveticaNeueStd','Barlow','Helvetica Neue',Arial,sans-serif" }), [tokens, TYPOGRAPHY]);
  const styleMonoRed          = useMemo(() => ({ fontSize: TYPOGRAPHY.caption, color: tokens.monoRed,    fontFamily: "'HelveticaNeueStd','Barlow','Helvetica Neue',Arial,sans-serif" }), [tokens, TYPOGRAPHY]);

  const connTime = (() => {
    if (entry.cancelled || entry.atd === "XCL") return "CNCL";
    const inTime  = entry.ata || entry.eta || entry.sta;
    const outTime = entry.atd || entry.etd || entry.std;
    if (!inTime || !outTime) return null;
    const [ah, am] = inTime.split(":").map(Number);
    const [dh, dm] = outTime.split(":").map(Number);
    if (isNaN(ah) || isNaN(am) || isNaN(dh) || isNaN(dm)) return null;
    let diff = (dh * 60 + dm) - (ah * 60 + am);
    if (diff < 0 && diff + 1440 < 480) diff += 1440;
    return fmtConn(diff);
  })();

  const mctRisk = getMctRisk(entry);
  const mctColor = mctRisk === "AT_RISK"
    ? { bg: tokens.dangerBgSubtle,        border: tokens.dangerBorder,           text: tokens.danger  }
    : mctRisk === "TIGHT"
      ? { bg: tokens.warningBgSubtle,     border: tokens.warningBorder,          text: tokens.warning }
      : mctRisk === "SAFE"
        ? { bg: "rgba(110,196,114,0.12)", border: "rgba(110,196,114,0.35)",      text: tokens.success }
        : null;

  // Departure urgency glow
  const now = useCurrentTime();
  const nowMins = now.getHours() * 60 + now.getMinutes();
  // For past dates (yesterday, etc.) treat all remaining flights as past departure time
  const effectiveNowMins = (() => {
    if (!appDate) return nowMins;
    const today = new Date().toLocaleDateString("en-CA", { timeZone: "America/Los_Angeles" });
    return appDate < today ? 1440 : nowMins;
  })();

  // Helper: parse departure time from entry (returns minutes, or null)
  const depMinsForEntry = (() => {
    if (entry.status && entry.status !== "PROTECTED") return null;
    if (appDate) {
      const today = new Date().toLocaleDateString("en-CA", { timeZone: "America/Los_Angeles" });
      if (appDate > today) return null; // future dates: no urgency
      // past dates: fall through so overdue status is preserved
    }
    const times = [entry.std, entry.etd].filter(Boolean);
    if (!times.length) return null;
    const toM = t => { const [h,m] = t.split(":").map(Number); return isNaN(h) ? null : h*60+m; };
    const val = Math.max(...times.map(toM).filter(v => v !== null));
    return val === -Infinity ? null : val;
  })();

  // Adjust departure time for next-day flights (e.g. 00:05 STD when it's currently 20:39)
  const depMinsAdjusted = (() => {
    if (depMinsForEntry === null) return null;
    if (depMinsForEntry < effectiveNowMins && depMinsForEntry + 1440 - effectiveNowMins < 480)
      return depMinsForEntry + 1440;
    return depMinsForEntry;
  })();

  // Glow (urgent pulse + solid overdue) only when outbound is the user's own airline
  const glowBase = (() => {
    if (!myAirline || depMinsAdjusted === null) return null;
    const outAl   = getAirlineCode(entry.outAirline, entry.outFlt);
    const myAl    = myAirline.toUpperCase();
    const myCodes = myAl === "LHG" ? [...LHG_CARRIERS] : [myAl];
    if (!myCodes.includes(outAl)) return null;
    return depMinsAdjusted;
  })();

  const isOverdue = glowBase !== null && effectiveNowMins > glowBase;
  const isUrgent  = glowBase !== null && !isOverdue && (glowBase - effectiveNowMins) <= 15;

  const entryIsComplete = !!(entry.status && entry.status !== "" && entry.status !== "PROTECTED");

  // Partner can request amenities when inbound is UA and flight is delayed
  const canRequestAmenity = !isUA && !!onAmenityRequest && !amenityStatus &&
    entry.inAirline === "UA" && entry.sta &&
    ((entry.eta && entry.eta > entry.sta) || (entry.ata && entry.ata > entry.sta));

  // Will Hold only available when the user's airline is the outbound carrier
  const canWillHold = onWillHold && !entryIsComplete && (() => {
    if (!myAirline) return false;
    const outAl = getAirlineCode(entry.outAirline, entry.outFlt);
    const myAl = myAirline.toUpperCase();
    const myCodes = myAl === "LHG" ? [...LHG_CARRIERS] : [myAl];
    return myCodes.includes(outAl);
  })();

  // Partner can mark boarded when card is urgent (yellow) or overdue (red) for their own outbound
  const canMarkBoarded = !isUA && (isUrgent || isOverdue) && onBoarded;
  // Show "✓ Boarded" badge if paxExp was set while card was urgent/overdue
  // Visible to everyone (UA + partner). Badge appears when paxExp is set AND
  // the card was ever in an urgent/overdue state (glowBase exists), even if
  // the flight has now departed.
  const showBoardedBadge = (() => {
    if (entry.paxExp === "" || entry.paxExp == null) return false;
    const n = Number(entry.paxExp);
    if (isNaN(n) || n < 0) return false;
    // Only show on cards that have/had a departure urgency context (glowBase not null, or already past)
    return glowBase !== null || (depMinsAdjusted !== null && effectiveNowMins > depMinsAdjusted);
  })();

  // Overdue badge: UA sees it on all cards; partners only on their own outbound
  const isOverdueBadge = (() => {
    if (depMinsAdjusted === null || effectiveNowMins <= depMinsAdjusted) return false;
    if (!myAirline) return false;
    const myAl = myAirline.toUpperCase();
    if (myAl === "UA") return true;
    const outAl   = getAirlineCode(entry.outAirline, entry.outFlt);
    const myCodes = myAl === "LHG" ? [...LHG_CARRIERS] : [myAl];
    return myCodes.includes(outAl);
  })();

  // Dim partner-only connections for UA SCCOs (neither flight is UA)
  const isPartnerOnly = isUA && (() => {
    const inAl  = getAirlineCode(entry.inAirline, entry.inFlt);
    const outAl = getAirlineCode(entry.outAirline, entry.outFlt);
    return inAl !== "UA" && outAl !== "UA";
  })();

  // Swipe state
  const [offsetX,     setOffsetX]     = useState(0);
  const [swiped,      setSwiped]      = useState(false);
  const [confirmDel,  setConfirmDel]  = useState(false);
  const touchStart    = useRef(null);
  const TRAY_WIDTH    = 150;

  const closeSwipe = () => { setOffsetX(0); setSwiped(false); setConfirmDel(false); };
  const [hovered, setHovered] = useState(false);
  const [expanded, setExpanded] = useState(false);
  const [menuOpen,    setMenuOpen]    = useState(false);
  const [menuClosing, setMenuClosing] = useState(false);
  const [menuPos,     setMenuPos]     = useState({ top: 0, left: 0 });
  const [menuDelConfirm, setMenuDelConfirm] = useState(false);
  const cardRef        = useRef(null);
  const longPressTimer = useRef(null);
  const longPressFired = useRef(false);
  const touchStartYRef = useRef(null);

  const immediateClose = () => { setMenuOpen(false); setMenuClosing(false); setMenuDelConfirm(false); };
  const closeMenu = () => { setMenuClosing(true); setMenuDelConfirm(false); _closeActiveMenu = null; };

  const openMenu = (pos) => {
    if (_closeActiveMenu) _closeActiveMenu();
    _closeActiveMenu = immediateClose;
    setMenuDelConfirm(false);
    setMenuClosing(false);
    setMenuPos(pos);
    setMenuOpen(true);
  };

  const onContextMenu = (e) => {
    e.preventDefault();
    const menuW = 220;
    const left = Math.max(8, Math.min(e.clientX, window.innerWidth - menuW - 8));
    const upward = e.clientY > window.innerHeight * 0.55;
    openMenu(upward
      ? { bottom: window.innerHeight - e.clientY + 4, left, upward: true }
      : { top: e.clientY, left, upward: false });
  };

  useEffect(() => {
    if (!menuOpen) return;
    const handler = (e) => {
      if (e.target.closest(".ctx-menu-panel")) return;
      closeMenu();
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, [menuOpen]); // eslint-disable-line react-hooks/exhaustive-deps -- closeMenu is stable; re-registering on every render would thrash the listener



  const onTouchStart = (e) => {
    touchStart.current   = e.touches[0].clientX;
    touchStartYRef.current = e.touches[0].clientY;
    longPressFired.current = false;
    const el = cardRef.current;
    longPressTimer.current = setTimeout(() => {
      if (!el) return;
      longPressFired.current = true;
      if (navigator.vibrate) navigator.vibrate(50);
      hapticMedium();
      const rect = el.getBoundingClientRect();
      const menuW = 220;
      const anchorY = rect.top + 8;
      const left = Math.max(8, Math.min(rect.left + (rect.width - menuW) / 2, window.innerWidth - menuW - 8));
      const upward = anchorY > window.innerHeight * 0.55;
      openMenu(upward
        ? { bottom: window.innerHeight - rect.top + 4, left, upward: true }
        : { top: anchorY, left, upward: false });
    }, 500);
  };
  const onTouchMove = (e) => {
    if (touchStart.current === null) return;
    const dx = e.touches[0].clientX - touchStart.current;
    const dy = Math.abs(e.touches[0].clientY - (touchStartYRef.current || 0));
    if ((Math.abs(dx) > 8 || dy > 8) && longPressTimer.current) {
      clearTimeout(longPressTimer.current);
      longPressTimer.current = null;
    }
    if (swiped) {
      if (dx > 0) setOffsetX(Math.min(-TRAY_WIDTH + dx, 0));
    } else {
      if (dx < 0) setOffsetX(Math.max(dx, -TRAY_WIDTH));
    }
  };
  const onTouchEnd = () => {
    if (longPressTimer.current) { clearTimeout(longPressTimer.current); longPressTimer.current = null; }
    if (!longPressFired.current) {
      if (swiped) {
        if (offsetX > -TRAY_WIDTH / 2) {
          closeSwipe();
        } else {
          setOffsetX(-TRAY_WIDTH);
        }
      } else {
        if (offsetX < -TRAY_WIDTH / 2) {
          setOffsetX(-TRAY_WIDTH);
          setSwiped(true);
        } else {
          setOffsetX(0);
          setSwiped(false);
        }
      }
    }
    touchStart.current   = null;
    touchStartYRef.current = null;
  };

  return (
    <div ref={cardRef} className={`entry-card${isNew ? " entry-card-new" : ""}${isImported ? " entry-card-imported" : ""}${isOverdue ? " entry-card-overdue" : isUrgent ? " entry-card-urgent" : ""}`} style={{ ...STYLE_ENTRY_OUTER, opacity: isPartnerOnly ? 0.75 : 1, boxShadow: (entry.status && entry.section !== "outlook") ? `-3px 0 12px -2px ${s.dot}33` : undefined }}>
      {/* Action tray behind the card */}
      <div style={{ position: "absolute", top: 0, right: 0, bottom: 0, width: TRAY_WIDTH,
        display: "flex", alignItems: "stretch", borderRadius: "0 14px 14px 0", overflow: "hidden",
        opacity: swiped ? 1 : 0, transition: "opacity 0.15s" }}>
        {isUA && confirmDel && (
          <>
            <Button variant="ghost" onClick={closeSwipe}
              style={{ flex: 1, color: SA.silver,
                fontSize: TYPOGRAPHY.caption, letterSpacing: "0.22em", textTransform: "uppercase",
                fontFamily: "'HelveticaNeueStd','Barlow','Helvetica Neue',Arial,sans-serif" }}>
              Cancel
            </Button>
            <Button onClick={() => { onDelete(entry.id); closeSwipe(); }} style={styleConfirmDeleteBtn}>
              Confirm
            </Button>
          </>
        )}
        {isUA && !confirmDel && (
          <Button onClick={() => setConfirmDel(true)} style={styleDeleteBtn}>
            Delete
          </Button>
        )}
        {!isUA && canMarkBoarded && (
          <Button onClick={() => { onBoarded(entry); closeSwipe(); }} style={stylePartnerTrayBtn}>
            {entry.paxExp != null && entry.paxExp !== "" ? "Edit Boarded" : "Report Boarded"}
          </Button>
        )}
        {!isUA && !canMarkBoarded && onRemark && (
          <Button onClick={() => { onRemark(entry); closeSwipe(); }} style={stylePartnerTrayBtn}>
            + Remark
          </Button>
        )}
      </div>

      {/* Card itself -- slides left on swipe */}
      <div
        onContextMenu={onContextMenu}
        onTouchStart={onTouchStart}
        onTouchMove={onTouchMove}
        onTouchEnd={onTouchEnd}
        onClick={swiped ? closeSwipe : () => { if (longPressFired.current) { longPressFired.current = false; return; } onMarkSeen?.(); setExpanded(e => !e); }}
        onMouseEnter={() => setHovered(true)}
        onMouseLeave={() => setHovered(false)}
        className="entry-card-inner glass-grain"
        style={{ ...GLASS.card,
          borderLeft: `3px solid ${entry.section === "outlook" || !entry.status ? SA.borderGray : s.dot}`,
          borderRadius: RADIUS.card, padding: 0, overflow: "hidden",
          transform: `translateX(${offsetX}px)${hovered && !swiped ? " scale(1.015)" : ""}`,
          transition: touchStart.current ? "none" : "transform 0.22s cubic-bezier(0.25,0.46,0.45,0.94)",
          position: "relative", isolation: "isolate",
          touchAction: "pan-y",
          cursor: "pointer" }}>
      <div style={{ padding: isWide ? "18px 20px 12px" : "14px 16px 10px" }}>
      {/* Desktop hover actions — partners only */}

      <div style={{ display: "flex", alignItems: "flex-start", marginBottom: 8, paddingRight: isWide ? 20 : 18 }}>
        <div style={{ display: "flex", flexDirection: isWide ? "row" : "column", alignItems: isWide ? "center" : "stretch", gap: isWide ? 10 : 5, flex: 1 }}>
          {/* Inbound */}
          <div style={{ display: "flex", alignItems: "center", gap: 7, minWidth: 0 }}>
            <AirlineLogo iata={entry.inAirline || (entry.inFlt || "").slice(0,2)} size={isWide ? 38 : 34} />
            <div style={{ minWidth: 0 }}>
              <div style={{ fontFamily: "'HelveticaNeueStd','Barlow','Helvetica Neue',Arial,sans-serif", fontSize: isWide ? 21 : 18, color: SA.white, fontWeight: 300, letterSpacing: "0.02em" }}>{entry.inFlt}</div>
              <div style={{ fontSize: isWide ? TYPOGRAPHY.sub : TYPOGRAPHY.caption, color: SA.silver, letterSpacing: "0.04em", marginTop: 1, textTransform: "uppercase", ...(isWide ? { whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" } : {}) }}>
                {(() => { const g = entry.inGate || terminalFallback(entry.inAirline || (entry.inFlt || "").slice(0,2)); return <span style={{ color: SA.accent, fontFamily: "'HelveticaNeueStd','Barlow','Helvetica Neue',Arial,sans-serif", fontSize: "0.85em", border: `1px solid ${SA.accent}`, borderRadius: RADIUS.pill, padding: "3px 6px", marginRight: 6, display: "inline-flex", alignItems: "center", verticalAlign: "middle" }}>{g}</span>; })()}{(entry.origin || "").toUpperCase()} > {entry.ata ? entry.ata + " ATA" : entry.eta ? entry.eta + " ETA" : entry.sta ? entry.sta + " STA" : "--"}
              </div>
            </div>
          </div>
          {/* Arrow */}
          {isWide ? (
            <div style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 2, flexShrink: 0 }}>
              <div style={{ width: 28, height: 1, background: SA.borderGray, opacity: 0.6 }} />
              <StarMark size={8} color={SA.borderGray} />
            </div>
          ) : (
            <div style={{ display: "flex", alignItems: "center", gap: 5, paddingLeft: 41 }}>
              <div style={{ width: 14, height: 1, background: SA.borderGray, opacity: 0.5 }} />
              <StarMark size={6} color={SA.borderGray} />
            </div>
          )}
          {/* Outbound */}
          <div style={{ display: "flex", alignItems: "center", gap: 7, minWidth: 0 }}>
            <div style={{ position: "relative" }}>
              <AirlineLogo iata={entry.outAirline || (entry.outFlt || "").slice(0,2)} size={isWide ? 38 : 34} />
              {entry._msgCount > 0 && (
                <span style={{ position: "absolute", top: -4, right: -4, background: tokens.dangerMuted,
                  color: SA.white, fontSize: 7, borderRadius: RADIUS.md, padding: "1px 4px",
                  fontFamily: "'HelveticaNeueStd','Barlow','Helvetica Neue',Arial,sans-serif", fontWeight: 700, minWidth: 12, textAlign: "center" }}>
                  {entry._msgCount}
                </span>
              )}
            </div>
            <div style={{ minWidth: 0 }}>
              <div style={{ fontFamily: "'HelveticaNeueStd','Barlow','Helvetica Neue',Arial,sans-serif", fontSize: isWide ? 21 : 18, color: SA.lightSilver, fontWeight: 300, letterSpacing: "0.02em" }}>{entry.outFlt}</div>
              <div style={{ fontSize: isWide ? TYPOGRAPHY.sub : TYPOGRAPHY.caption, color: SA.silver, letterSpacing: "0.04em", marginTop: 1, textTransform: "uppercase", ...(isWide ? { whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" } : {}) }}>
                {(() => { const g = entry.outGate || terminalFallback(entry.outAirline || (entry.outFlt || "").slice(0,2)); return <span style={{ color: SA.accent, fontFamily: "'HelveticaNeueStd','Barlow','Helvetica Neue',Arial,sans-serif", fontSize: "0.85em", border: `1px solid ${SA.accent}`, borderRadius: RADIUS.pill, padding: "3px 6px", marginRight: 6, display: "inline-flex", alignItems: "center", verticalAlign: "middle" }}>{g}</span>; })()}{(entry.dest || "").toUpperCase()} > {entry.atd ? entry.atd + " ATD" : entry.etd ? entry.etd + " ETD" : entry.std ? entry.std + " STD" : "--"}
              </div>
            </div>
          </div>
        </div>
      </div>
      {entry.pinned && (
        <svg width="13" height="13" viewBox="0 0 16 16"
          style={{ position: "absolute", top: 13, right: 34, pointerEvents: "none" }}
          title="Pinned">
          <path fillRule="evenodd" clipRule="evenodd"
            d="M8 1.5C5.0 1.5 3 3.5 3 6C3 9.5 8 15 8 15C8 15 13 9.5 13 6C13 3.5 11.0 1.5 8 1.5ZM8 4C9.1 4 10 4.9 10 6C10 7.1 9.1 8 8 8C6.9 8 6 7.1 6 6C6 4.9 6.9 4 8 4Z"
            fill={tokens.warning}/>
        </svg>
      )}
      {isNew && (
        <span style={{
          position: "absolute", top: 11, right: 34,
          background: "rgba(74,124,247,0.15)", color: "#4A7CF7",
          border: "1px solid rgba(74,124,247,0.4)",
          padding: "2px 7px", borderRadius: RADIUS.pill,
          fontSize: TYPOGRAPHY.caption, fontWeight: 700, letterSpacing: "0.12em",
          fontFamily: "'HelveticaNeueStd','Barlow','Helvetica Neue',Arial,sans-serif", lineHeight: 1.5,
          pointerEvents: "none",
        }}>NEW</span>
      )}
      <svg width="14" height="14" viewBox="0 0 14 14" fill="none"
        style={{ position: "absolute", top: 14, right: 14,
          transition: "transform 0.2s", transform: expanded ? "rotate(180deg)" : "rotate(0deg)",
          opacity: 0.45, pointerEvents: "none" }}>
        <path d="M2.5 5L7 9.5L11.5 5" stroke={SA.silver} strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round"/>
      </svg>
      {/* Status + connection time row */}
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", flexWrap: "wrap", gap: 6, marginBottom: expanded ? 10 : 0 }}>
        {isOverdueBadge ? (
          <span style={styleOverdueBadge}>
            <svg width="10" height="10" viewBox="0 0 12 12" fill="none">
              <circle cx="6" cy="6" r="5" stroke={tokens.danger} strokeWidth="1.4"/>
              <path d="M6 3.5V6.5" stroke={tokens.danger} strokeWidth="1.4" strokeLinecap="round"/>
              <circle cx="6" cy="8.5" r="0.7" fill={tokens.danger}/>
            </svg>
            Overdue
          </span>
        ) : entry.status ? (
          <span style={statusBadgeStyle}>
            <span style={{ width: 6, height: 6, borderRadius: RADIUS.circle, background: s.dot, display: "inline-block" }} />
            {entry.status}
          </span>
        ) : (
          <span style={styleMonitoringBadge}>
            <span style={styleMonitoringDot} />
            Monitoring
          </span>
        )}
        <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
          {showBoardedBadge && (() => {
            const bn = Number(entry.paxExp);
            const tn = Number(entry.ttlPax) || 0;
            const bc = bn === 0
              ? { bg: tokens.dangerBgSubtle, border: tokens.dangerBorder, text: tokens.dangerMuted }
              : tn > 0 && bn >= tn
                ? { bg: "rgba(110,196,114,0.12)", border: "rgba(110,196,114,0.35)", text: tokens.success }
                : { bg: tokens.warningBgSubtle, border: "rgba(200,168,48,0.35)", text: tokens.warning };
            return (
              <span style={{ background: bc.bg, color: bc.text,
                border: `1px solid ${bc.border}`,
                padding: "4px 10px", borderRadius: RADIUS.pill,
                fontSize: TYPOGRAPHY.label, fontWeight: 700, letterSpacing: "0.08em",
                fontFamily: "'HelveticaNeueStd','Barlow','Helvetica Neue',Arial,sans-serif" }}>
                {entry.paxExp} BOARDED
              </span>
            );
          })()}
          {amenityStatus && (
            <span style={{
              padding: "4px 10px", borderRadius: RADIUS.pill,
              fontSize: TYPOGRAPHY.caption, fontWeight: 700, letterSpacing: "0.08em",
              fontFamily: "'HelveticaNeueStd','Barlow','Helvetica Neue',Arial,sans-serif", whiteSpace: "nowrap",
              background: amenityStatus === "approved"
                ? "rgba(110,196,114,0.12)"
                : amenityStatus === "denied"
                  ? tokens.dangerBgSubtle
                  : tokens.warningBgSubtle,
              color: amenityStatus === "approved"
                ? tokens.success
                : amenityStatus === "denied"
                  ? tokens.danger
                  : tokens.warning,
              border: `1px solid ${amenityStatus === "approved"
                ? "rgba(110,196,114,0.35)"
                : amenityStatus === "denied"
                  ? tokens.dangerBorder
                  : tokens.warningBorder}`,
            }}>
              {amenityStatus === "approved" ? "AMENITIES ✓"
                : amenityStatus === "denied" ? "AMENITIES ✗"
                : "AMENITIES?"}
            </span>
          )}
          {entry.willHold && entry.holdUntil && (
            <span style={{ background: tokens.partnerSurface, color: tokens.partner,
              border: `1px solid ${tokens.partnerBorder}`,
              padding: "4px 10px", borderRadius: RADIUS.pill,
              fontSize: TYPOGRAPHY.caption, fontWeight: 700, letterSpacing: "0.08em",
              fontFamily: "'HelveticaNeueStd','Barlow','Helvetica Neue',Arial,sans-serif", whiteSpace: "nowrap" }}>
              HOLD · {entry.holdUntil}
            </span>
          )}
          {connTime && (
            <span style={{
              background: connTime === "CNCL"
                ? tokens.dangerBgSubtle
                : mctColor
                  ? mctColor.bg
                  : connTime.startsWith("-")
                    ? tokens.warningBgSubtle
                    : SA.darkGray,
              border: `1px solid ${connTime === "CNCL"
                ? tokens.dangerBorder
                : mctColor
                  ? mctColor.border
                  : connTime.startsWith("-")
                    ? tokens.warningBorder
                    : SA.borderGray}`,
              color: connTime === "CNCL" ? tokens.monoRed : mctColor ? mctColor.text : connTime.startsWith("-") ? tokens.warning : SA.white,
              padding: "5px 12px", borderRadius: RADIUS.pill,
              fontSize: TYPOGRAPHY.caption, fontWeight: 700,
              fontFamily: "'HelveticaNeueStd','Barlow','Helvetica Neue',Arial,sans-serif",
              letterSpacing: "0.04em",
            }}>
              {connTime === "CNCL" ? "CNCL" : connTime}
              {connTime !== "CNCL" && <span style={{ fontSize: TYPOGRAPHY.label, color: mctColor ? mctColor.text : SA.silver, marginLeft: 4, fontWeight: 400, letterSpacing: "0.08em" }}>CONN</span>}
            </span>
          )}
        </div>
      </div>
      </div>{/* end header zone */}
      {expanded && (<div style={{
        background: isDark ? "rgba(0,0,0,0.22)" : "rgba(0,0,0,0.04)",
        borderTop: `1px solid ${isDark ? "rgba(255,255,255,0.07)" : "rgba(0,0,0,0.07)"}`,
        padding: isWide ? "14px 20px 18px" : "12px 16px 14px",
      }}><div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 6 }}>
        {/* PAX block */}
        {(() => {
          const ttl = Number(entry.ttlPax) || 0;
          const exp = Number(entry.paxExp) || 0;
          const rbk = Number(entry.paxRbk) || 0;
          const accounted = exp + rbk;
          const unaccounted = ttl > 0 ? ttl - accounted : 0;
          const pct = ttl > 0 ? Math.round(accounted / ttl * 100) : null;
          return (
            <div style={{ background: SA.darkGray, borderRadius: RADIUS.md, padding: "10px 12px" }}>
              <div style={{ fontSize: TYPOGRAPHY.tiny, color: SA.silver, letterSpacing: "0.22em", textTransform: "uppercase",
                fontFamily: "'Barlow','HelveticaNeueStd','Barlow','Helvetica Neue',Arial,sans-serif", marginBottom: 5 }}>
                Passengers at Risk
              </div>
              <div style={{ fontFamily: "'HelveticaNeueStd','Barlow','Helvetica Neue',Arial,sans-serif", fontSize: TYPOGRAPHY.header, color: SA.white, fontWeight: 600 }}>
                {ttl > 0 ? ttl : "--"}
              </div>
              <div style={{ display: "flex", gap: 8, marginTop: 5, flexWrap: "wrap" }}>
                {entry.paxExp != null && (
                  <span style={styleMonoGreen}>
                    {exp} exp
                  </span>
                )}
                {entry.paxRbk != null && (
                  <span style={styleMonoPurple}>
                    {rbk} rbk
                  </span>
                )}
                {ttl > 0 && unaccounted > 0 && (
                  <span style={styleMonoRed}>
                    {unaccounted} unk
                  </span>
                )}
              </div>
              {pct !== null && (
                <div style={{ marginTop: 6, height: 2, background: SA.borderGray, borderRadius: 1 }}>
                  <div style={{ height: 2, width: `${Math.min(pct,100)}%`, background: pct === 100 ? tokens.successAlt : tokens.warning, borderRadius: 1, transition: "width 0.3s" }} />
                </div>
              )}
            </div>
          );
        })()}
        {/* Bags block */}
        {(() => {
          const ttl = Number(entry.ttlBags) || 0;
          const exp = Number(entry.bagsExp) || 0;
          const unaccounted = ttl > 0 ? ttl - exp : 0;
          const pct = ttl > 0 ? Math.round(exp / ttl * 100) : null;
          return (
            <div style={{ background: SA.darkGray, borderRadius: RADIUS.md, padding: "10px 12px" }}>
              <div style={{ fontSize: TYPOGRAPHY.tiny, color: SA.silver, letterSpacing: "0.22em", textTransform: "uppercase",
                fontFamily: "'Barlow','HelveticaNeueStd','Barlow','Helvetica Neue',Arial,sans-serif", marginBottom: 5 }}>
                Bags at Risk
              </div>
              <div style={{ fontFamily: "'HelveticaNeueStd','Barlow','Helvetica Neue',Arial,sans-serif", fontSize: 15, color: SA.white, fontWeight: 600 }}>
                {ttl > 0 ? ttl : "--"}
              </div>
              <div style={{ display: "flex", gap: 8, marginTop: 5 }}>
                {entry.bagsExp != null && (
                  <span style={styleMonoGreen}>
                    {exp} exp
                  </span>
                )}
                {ttl > 0 && unaccounted > 0 && (
                  <span style={styleMonoRed}>
                    {unaccounted} missing
                  </span>
                )}
              </div>
              {pct !== null && (
                <div style={{ marginTop: 6, height: 2, background: SA.borderGray, borderRadius: 1 }}>
                  <div style={{ height: 2, width: `${Math.min(pct,100)}%`, background: pct === 100 ? tokens.successAlt : tokens.warning, borderRadius: 1, transition: "width 0.3s" }} />
                </div>
              )}
            </div>
          );
        })()}
      </div>
      {(entry.origin || entry.dest) && (
        <div style={{
          marginTop: 10, borderRadius: RADIUS.md, overflow: "hidden",
          border: `1px solid ${isDark ? "rgba(255,255,255,0.08)" : "rgba(80,100,160,0.18)"}`,
          boxShadow: isDark ? "0 2px 14px rgba(0,0,0,0.35)" : "0 2px 10px rgba(0,0,0,0.10)",
        }}>
          <FlightRouteMap origin={entry.origin} dest={entry.dest} isDark={isDark} />
        </div>
      )}
      {entry.remark && (
        <div style={{ marginTop: 9, borderTop: `1px solid ${SA.borderGray}`, paddingTop: 8 }}>
          {entry.remark.split("\n").filter(Boolean).map((line) => {
            const match = line.match(REMARK_PATTERN);
            return match ? (
              <div key={line} style={{ display: "flex", gap: 6, marginBottom: 4, alignItems: "flex-start" }}>
                <span style={{ fontSize: TYPOGRAPHY.label, color: SA.silver, fontFamily: "'HelveticaNeueStd','Barlow','Helvetica Neue',Arial,sans-serif",
                  letterSpacing: "0.04em", flexShrink: 0, marginTop: 1 }}>
                  [{match[1]} {match[2]}]
                </span>
                <span style={{ fontSize: TYPOGRAPHY.body, color: SA.lightSilver, lineHeight: 1.4, textTransform: "uppercase", letterSpacing: "0.04em" }}>{match[3]}</span>
              </div>
            ) : (
              <div key={line} style={{ fontSize: TYPOGRAPHY.body, color: SA.lightSilver, lineHeight: 1.5,
                fontFamily: "'HelveticaNeueStd','Barlow','Helvetica Neue',Arial,sans-serif", marginBottom: 4, textTransform: "uppercase", letterSpacing: "0.04em" }}>{line}</div>
            );
          })}
        </div>
      )}
      {/* SCCO stamp — subtle footer */}
      {entry.scco_name && (
        <div style={{ marginTop: 8, display: "flex", justifyContent: "flex-end" }}>
          <span style={{ fontSize: TYPOGRAPHY.label, color: SA.borderGray, fontFamily: "'Barlow',sans-serif",
            letterSpacing: "0.04em" }}>
            SCCO: {entry.scco_name}
          </span>
        </div>
      )}
      {/* Action buttons in expanded view */}
      <div style={{ marginTop: 12, display: "flex", gap: 6, justifyContent: "flex-end" }}>
        {isUA && onEdit && (
          <Button onClick={e => { e.stopPropagation(); onEdit(entry); }} style={editBtnStyle}>
            Edit
          </Button>
        )}
        {!isUA && canMarkBoarded && (
          <Button onClick={e => { e.stopPropagation(); onBoarded(entry); }} style={partnerExpandBtnStyle}>
            {entry.paxExp != null && entry.paxExp !== "" ? "Edit Boarded" : "Report Boarded"}
          </Button>
        )}
        {!isUA && !canMarkBoarded && onRemark && (
          <Button onClick={e => { e.stopPropagation(); onRemark(entry); }} style={partnerExpandBtnStyle}>
            + Remark
          </Button>
        )}
        {canRequestAmenity && (
          <Button onClick={e => { e.stopPropagation(); onAmenityRequest(entry); }} style={partnerExpandBtnStyle}>
            Request Amenities
          </Button>
        )}
        {canWillHold && (
          <Button onClick={e => { e.stopPropagation(); onWillHold(entry); }} style={partnerExpandBtnStyle}>
            {entry.willHold && entry.holdUntil ? "Edit Hold" : "Will Hold"}
          </Button>
        )}
      </div>
      </div>)}
      </div>

      {/* Long-press context menu — fixed, outside the transform div so position: fixed works correctly */}
      {menuOpen && (() => {
        const divider = <div style={{ height: 1, background: isDark ? "rgba(255,255,255,0.07)" : "rgba(0,0,0,0.07)", margin: "0 12px" }} />;
        const itemStyle = (color) => ({
          display: "flex", alignItems: "center", gap: 12,
          padding: "13px 16px", width: "100%",
          background: "transparent", border: "none", cursor: "pointer",
          color, fontFamily: "'HelveticaNeueStd','Barlow','Helvetica Neue',Arial,sans-serif",
          fontSize: 16, fontWeight: 500, letterSpacing: "0.01em", textAlign: "left",
          WebkitTapHighlightColor: "transparent",
        });
        const iconStyle = { display: "flex", flexShrink: 0, opacity: 0.65 };
        // Icons
        const iconEdit    = <svg width="16" height="16" viewBox="0 0 16 16" fill="none"><path d="M11 2.5L13.5 5L6.5 12H4V9.5L11 2.5Z" stroke="currentColor" strokeWidth="1.3" strokeLinejoin="round"/><path d="M9.5 4L12 6.5" stroke="currentColor" strokeWidth="1.3"/></svg>;
        const iconPin     = <svg width="16" height="16" viewBox="0 0 16 16"><path fillRule="evenodd" clipRule="evenodd" d="M8 1.5C5.0 1.5 3 3.5 3 6C3 9.5 8 15 8 15C8 15 13 9.5 13 6C13 3.5 11.0 1.5 8 1.5ZM8 4C9.1 4 10 4.9 10 6C10 7.1 9.1 8 8 8C6.9 8 6 7.1 6 6C6 4.9 6.9 4 8 4Z" fill="currentColor"/></svg>;
        const iconBoarded = <svg width="16" height="16" viewBox="0 0 16 16" fill="none"><circle cx="8" cy="8" r="5.5" stroke="currentColor" strokeWidth="1.3"/><path d="M5.5 8L7.5 10L10.5 6" stroke="currentColor" strokeWidth="1.3" strokeLinecap="round" strokeLinejoin="round"/></svg>;
        const iconRemark  = <svg width="16" height="16" viewBox="0 0 16 16" fill="none"><path d="M3 12.5V3.5C3 2.948 3.448 2.5 4 2.5H10L13 5.5V12.5C13 13.052 12.552 13.5 12 13.5H4C3.448 13.5 3 13.052 3 12.5Z" stroke="currentColor" strokeWidth="1.3"/><path d="M10 2.5V5.5H13" stroke="currentColor" strokeWidth="1.2" strokeLinejoin="round"/><path d="M6 8H10M6 10H8.5" stroke="currentColor" strokeWidth="1.2" strokeLinecap="round"/></svg>;
        const iconMessage = <svg width="16" height="16" viewBox="0 0 16 16" fill="none"><path d="M2.5 3.5C2.5 2.948 2.948 2.5 3.5 2.5H12.5C13.052 2.5 13.5 2.948 13.5 3.5V9.5C13.5 10.052 13.052 10.5 12.5 10.5H5.5L2.5 13V3.5Z" stroke="currentColor" strokeWidth="1.3" strokeLinejoin="round"/></svg>;
        const iconHold     = <svg width="16" height="16" viewBox="0 0 16 16" fill="none"><circle cx="8" cy="8" r="5.5" stroke="currentColor" strokeWidth="1.3"/><path d="M8 5V8.5H11" stroke="currentColor" strokeWidth="1.3" strokeLinecap="round" strokeLinejoin="round"/></svg>;
        const iconAmenity  = <svg width="16" height="16" viewBox="0 0 16 16" fill="none"><rect x="2" y="7" width="12" height="7" rx="1" stroke="currentColor" strokeWidth="1.3"/><path d="M5 7V5.5C5 3.567 6.567 2 8 2C9.433 2 11 3.567 11 5.5V7" stroke="currentColor" strokeWidth="1.3" strokeLinecap="round"/><path d="M2 10.5H14" stroke="currentColor" strokeWidth="1.1" strokeLinecap="round" strokeDasharray="2 2"/></svg>;
        const iconTrash   = <svg width="18" height="18" viewBox="0 0 18 18" fill="none"><path d="M3.5 5.5H14.5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/><path d="M6.5 5.5V4C6.5 3.724 6.724 3.5 7 3.5H11C11.276 3.5 11.5 3.724 11.5 4V5.5" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round"/><path d="M4.5 5.5L5 13.5C5 14.052 5.448 14.5 6 14.5H12C12.552 14.5 13 14.052 13 13.5L13.5 5.5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/><path d="M7.5 8V12M10.5 8V12" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round"/></svg>;
        const iconView    = <svg width="16" height="16" viewBox="0 0 16 16" fill="none"><ellipse cx="8" cy="8" rx="5.5" ry="3.5" stroke="currentColor" strokeWidth="1.3"/><circle cx="8" cy="8" r="1.5" fill="currentColor"/></svg>;
        const iconHide    = <svg width="16" height="16" viewBox="0 0 16 16" fill="none"><path d="M1 8s2.5-4.5 7-4.5S15 8 15 8s-2.5 4.5-7 4.5S1 8 1 8z" stroke="currentColor" strokeWidth="1.3"/><circle cx="8" cy="8" r="2" stroke="currentColor" strokeWidth="1.3"/><path d="M2.5 2.5L13.5 13.5" stroke="currentColor" strokeWidth="1.3" strokeLinecap="round"/></svg>;
        const iconReopen  = <svg width="16" height="16" viewBox="0 0 16 16" fill="none"><path d="M3 8C3 5.239 5.239 3 8 3C9.89 3 11.54 4.02 12.42 5.5" stroke="currentColor" strokeWidth="1.3" strokeLinecap="round"/><path d="M11 5.5H13.5V3" stroke="currentColor" strokeWidth="1.3" strokeLinecap="round" strokeLinejoin="round"/><path d="M13 8C13 10.761 10.761 13 8 13C6.11 13 4.46 11.98 3.58 10.5" stroke="currentColor" strokeWidth="1.3" strokeLinecap="round"/></svg>;
        const iconSuccess = <svg width="16" height="16" viewBox="0 0 16 16" fill="none"><circle cx="8" cy="8" r="5.5" stroke="currentColor" strokeWidth="1.3"/><path d="M5 8L7 10.5L11 5.5" stroke="currentColor" strokeWidth="1.3" strokeLinecap="round" strokeLinejoin="round"/></svg>;
        const iconTrackOut = <svg width="16" height="16" viewBox="0 0 16 16" fill="none"><path d="M2 11L6.5 7.5L9.5 9.5L14 4" stroke="currentColor" strokeWidth="1.3" strokeLinecap="round" strokeLinejoin="round"/><path d="M11 4H14V7" stroke="currentColor" strokeWidth="1.3" strokeLinecap="round" strokeLinejoin="round"/></svg>;
        const iconTrackIn  = <svg width="16" height="16" viewBox="0 0 16 16" fill="none"><path d="M2 5L6.5 8.5L9.5 6.5L14 10" stroke="currentColor" strokeWidth="1.3" strokeLinecap="round" strokeLinejoin="round"/><path d="M11 10H14V7" stroke="currentColor" strokeWidth="1.3" strokeLinecap="round" strokeLinejoin="round"/></svg>;
        const iconExpand  = <svg width="16" height="16" viewBox="0 0 16 16" fill="none"><path d="M4 6L8 10L12 6" stroke="currentColor" strokeWidth="1.3" strokeLinecap="round" strokeLinejoin="round" style={{ transform: expanded ? "rotate(180deg)" : "none", transformOrigin: "8px 8px" }}/></svg>;

        return createPortal(
          <div
            className="ctx-menu-panel"
            style={{
              position: "fixed",
              ...(menuPos.upward ? { bottom: menuPos.bottom } : { top: menuPos.top }),
              left: menuPos.left, width: 220,
              zIndex: 201,
                ...GLASS.card,
                borderRadius: RADIUS.card,
                padding: 0,
                overflow: "hidden",
                boxSizing: "border-box",
                transformOrigin: "top center",
                animation: menuClosing
                  ? "menuFadeOut 0.18s cubic-bezier(0.4,0,1,1) forwards"
                  : "menuSpringIn 0.32s cubic-bezier(0.34,1.56,0.64,1) forwards",
              }}
              onAnimationEnd={() => { if (menuClosing) { setMenuOpen(false); setMenuClosing(false); } }}
            >
              {/* Primary actions */}
              {isUA && onEdit && (
                <button className="ctx-menu-btn" style={itemStyle(SA.white)} onClick={(e) => { e.stopPropagation(); closeMenu(); onEdit(entry); }}>
                  <span style={iconStyle}>{iconEdit}</span>Edit
                </button>
              )}
              {isUA && onMarkSuccessful && entry.status !== "SUCCESS" && !entry.cancelled && (<>
                {onEdit && divider}
                <button className="ctx-menu-btn" style={itemStyle(SA.white)} onClick={(e) => { e.stopPropagation(); closeMenu(); onMarkSuccessful(entry); }}>
                  <span style={iconStyle}>{iconSuccess}</span>Mark Successful
                </button>
              </>)}
              {isUA && onPin && (<>
                {onEdit && divider}
                <button className="ctx-menu-btn" style={itemStyle(entry.pinned ? tokens.warning : SA.white)} onClick={(e) => { e.stopPropagation(); closeMenu(); onPin(entry.id, !entry.pinned); }}>
                  <span style={iconStyle}>{iconPin}</span>{entry.pinned ? "Unpin" : "Pin to Top"}
                </button>
              </>)}
              {!isUA && canMarkBoarded && onBoarded && (
                <button className="ctx-menu-btn" style={itemStyle(SA.white)} onClick={(e) => { e.stopPropagation(); closeMenu(); onBoarded(entry); }}>
                  <span style={iconStyle}>{iconBoarded}</span>
                  {entry.paxExp != null && entry.paxExp !== "" ? "Edit Boarded" : "Report Boarded"}
                </button>
              )}
              {onRemark && (<>
                {(canMarkBoarded && onBoarded) && divider}
                <button className="ctx-menu-btn" style={itemStyle(SA.white)} onClick={(e) => { e.stopPropagation(); closeMenu(); onRemark(entry); }}>
                  <span style={iconStyle}>{iconRemark}</span>Add Remark
                </button>
              </>)}
              {isUA && onHide && (<>
                {divider}
                <button className="ctx-menu-btn" style={itemStyle(SA.white)} onClick={(e) => { e.stopPropagation(); closeMenu(); onHide(entry); }}>
                  <span style={iconStyle}>{iconHide}</span>Hide card
                </button>
              </>)}
              {/* Request Amenities */}
              {canRequestAmenity && (<>
                {divider}
                <button className="ctx-menu-btn" style={itemStyle(tokens.partner)} onClick={(e) => { e.stopPropagation(); closeMenu(); onAmenityRequest(entry); }}>
                  <span style={iconStyle}>{iconAmenity}</span>Request Amenities
                </button>
              </>)}
              {/* Will Hold */}
              {canWillHold && (<>
                {divider}
                <button className="ctx-menu-btn" style={itemStyle(SA.white)} onClick={(e) => { e.stopPropagation(); closeMenu(); onWillHold(entry); }}>
                  <span style={iconStyle}>{iconHold}</span>
                  {entry.willHold && entry.holdUntil ? "Edit Hold" : "Will Hold"}
                </button>
              </>)}
              {/* Message */}
              {onMessage && (<>
                {divider}
                <button className="ctx-menu-btn" style={itemStyle(SA.white)} onClick={(e) => { e.stopPropagation(); closeMenu(); onMessage(entry); }}>
                  <span style={iconStyle}>{iconMessage}</span>Link to Message
                </button>
              </>)}
              {/* View Details */}
              {onView && !isUA && (<>
                {divider}
                <button className="ctx-menu-btn" style={itemStyle(SA.white)} onClick={(e) => { e.stopPropagation(); closeMenu(); onView(entry); }}>
                  <span style={iconStyle}>{iconView}</span>View Details
                </button>
              </>)}
              {/* Reopen */}
              {isUA && onReopen && entryIsComplete && (<>
                {divider}
                <button className="ctx-menu-btn" style={itemStyle(SA.white)} onClick={(e) => { e.stopPropagation(); closeMenu(); onReopen(entry); }}>
                  <span style={iconStyle}>{iconReopen}</span>Reopen
                </button>
              </>)}
              {/* FlightAware tracking */}
              {entry.inFlt && (<>
                {divider}
                <button className="ctx-menu-btn" style={itemStyle(SA.white)} onClick={(e) => { e.stopPropagation(); closeMenu(); window.open(`https://www.flightaware.com/live/flight/${toICAO(entry.inAirline)}${(entry.inFlt || "").replace(/^[A-Za-z]+/, "")}`, "_blank", "noopener,noreferrer"); }}>
                  <span style={iconStyle}>{iconTrackIn}</span>Track Inbound
                </button>
              </>)}
              {entry.outFlt && (<>
                {divider}
                <button className="ctx-menu-btn" style={itemStyle(SA.white)} onClick={(e) => { e.stopPropagation(); closeMenu(); window.open(`https://www.flightaware.com/live/flight/${toICAO(entry.outAirline)}${(entry.outFlt || "").replace(/^[A-Za-z]+/, "")}`, "_blank", "noopener,noreferrer"); }}>
                  <span style={iconStyle}>{iconTrackOut}</span>Track Outbound
                </button>
              </>)}
              {/* Expand / Collapse */}
              {divider}
              <button className="ctx-menu-btn" style={itemStyle(SA.white)} onClick={(e) => { e.stopPropagation(); setExpanded(v => !v); closeMenu(); }}>
                <span style={iconStyle}>{iconExpand}</span>{expanded ? "Collapse" : "Expand"}
              </button>
              {/* Delete */}
              {isUA && onDelete && (<>
                <div style={{ height: 1, background: isDark ? "rgba(255,255,255,0.14)" : "rgba(0,0,0,0.12)", margin: "0 12px" }} />
                {menuDelConfirm
                  ? <button className="ctx-menu-btn" style={itemStyle(tokens.danger)} onClick={(e) => { e.stopPropagation(); onDelete(entry.id); closeMenu(); }}>
                      <span style={iconStyle}>{iconTrash}</span><strong>Confirm Delete</strong>
                    </button>
                  : <button className="ctx-menu-btn" style={itemStyle(tokens.dangerMuted)} onClick={(e) => { e.stopPropagation(); setMenuDelConfirm(true); }}>
                      <span style={iconStyle}>{iconTrash}</span>Delete
                    </button>
                }
              </>)}
          </div>,
          document.body
        );
      })()}
    </div>
  );
}

export { EntryCard };
